import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { AnimatePresence, motion, useMotionValue } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { useAssistant } from "@/contexts/AssistantContext";
import { BubbleButton } from "@/components/assistant/BubbleButton";
import { AssistantPanel } from "@/components/assistant/AssistantPanel";

const HIDDEN_PREFIXES = ["/admin", "/prof", "/login", "/register", "/profils", "/messages"];
const ALLOWED = ["client", "super_admin", "shop_admin"];
const MARGIN = 20;
const POS_KEY = "su_assistant_pos";

function bubbleSize() {
    return window.innerWidth < 640 ? 56 : 66;
}

function clampPos(x, y) {
    const s = bubbleSize();
    const maxX = window.innerWidth - s - MARGIN * 2;
    const maxY = window.innerHeight - s - MARGIN * 2;
    return [Math.min(0, Math.max(-maxX, x)), Math.min(0, Math.max(-maxY, y))];
}

function useBubblePosition() {
    const saved = (() => { try { return JSON.parse(localStorage.getItem(POS_KEY)) || [0, 0]; } catch { return [0, 0]; } })();
    const [ix, iy] = clampPos(saved[0], saved[1]);
    const x = useMotionValue(ix);
    const y = useMotionValue(iy);
    useEffect(() => {
        const onResize = () => { const [cx, cy] = clampPos(x.get(), y.get()); x.set(cx); y.set(cy); };
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, [x, y]);
    const save = () => {
        const [cx, cy] = clampPos(x.get(), y.get());
        x.set(cx); y.set(cy);
        localStorage.setItem(POS_KEY, JSON.stringify([cx, cy]));
    };
    return { x, y, save };
}

export function AssistantBubble() {
    const { user } = useAuth();
    const { open, setOpen } = useAssistant();
    const { pathname } = useLocation();
    const { x, y, save } = useBubblePosition();
    const bubbleRef = useRef(null);
    const draggedRef = useRef(false);
    const [anchor, setAnchor] = useState(null);
    const [size, setSize] = useState(bubbleSize);
    const sideOf = () => (window.innerWidth - MARGIN - bubbleSize() / 2 + x.get() > window.innerWidth / 2 ? "right" : "left");
    const [side, setSide] = useState(sideOf);

    const visible = user && ALLOWED.includes(user.role) && !HIDDEN_PREFIXES.some((p) => pathname.startsWith(p));

    useEffect(() => {
        const measure = () => {
            setSize(bubbleSize());
            setSide(sideOf());
            if (!open || !bubbleRef.current) return;
            const r = bubbleRef.current.getBoundingClientRect();
            setAnchor({
                right: r.left + r.width / 2 > window.innerWidth / 2,
                bottom: r.top + r.height / 2 > window.innerHeight / 2,
                rect: r,
            });
        };
        measure();
        window.addEventListener("resize", measure);
        return () => window.removeEventListener("resize", measure);
    }, [open]);

    if (!visible) return null;

    return (
        <>
            <AnimatePresence>{open && anchor && <AssistantPanel anchor={anchor} onClose={() => setOpen(false)} />}</AnimatePresence>
            <motion.div
                ref={bubbleRef}
                drag
                dragMomentum={false}
                dragElastic={0}
                style={{ x, y, position: "fixed", right: MARGIN, bottom: MARGIN, zIndex: 60, touchAction: "none" }}
                onDragStart={() => { draggedRef.current = true; }}
                onDragEnd={() => { save(); setSide(sideOf()); setTimeout(() => { draggedRef.current = false; }, 50); }}
                data-testid="assistant-bubble"
            >
                <BubbleButton size={size} side={side} open={open} onClick={() => { if (!draggedRef.current) setOpen(!open); }} />
            </motion.div>
        </>
    );
}
